<script setup>
import { getImagePath } from '@/common/constants/ConstCode';
import Util from '@/common/utils/Util';
import { toVn } from '@/common/utils/Currency';
import { useI18n } from 'vue-i18n';
import { useNavigator } from '@/common/composables/useNavigator';

const { t } = useI18n();
const { toStoreMain } = useNavigator();

const props = defineProps({
  storeInfo: {
    type: Object,
    required: true
  },
  userPosition: {
    type: Object,
    required: true
  }
});

const goToProduct = ({ storeNo }) => {
  toStoreMain(`/shop/${storeNo}/product`, true);
};

const calcDeliveryFee = (item) => {
  const free = t('search.result.card.free');
  const {
    baseDeliveryFee,
    baseDeliveryFeeLimit,
    increaseDeliveryFeePer,
    additionalDeliveryFee,
    maximumDeliveryFee,
    latitude,
    longitude
  } = item;
  const user = props.userPosition;

  // MEMO: 상점리스트 배달팁 계산
  const distance = Math.round(Util.getDistance(user.latitude, user.longitude, latitude, longitude).toFixed(1));

  let fee = baseDeliveryFee; // 배달팁

  if (distance <= baseDeliveryFeeLimit) return fee == 0 ? free : toVn(Number(fee));
  let calc = distance - baseDeliveryFeeLimit;

  while (increaseDeliveryFeePer > 0 && calc > 0) {
    calc -= increaseDeliveryFeePer;
    fee += additionalDeliveryFee;
  }

  if (fee >= maximumDeliveryFee) return maximumDeliveryFee == 0 ? free : maximumDeliveryFee;

  return fee == 0 ? free : toVn(Number(fee));
};

const formatBusinessTime = (schedule = []) => {
  const [businessTime] = schedule ?? [];
  if (!businessTime?.open || !businessTime?.close) return '';

  const open = businessTime.open.slice(0, 5);
  const close = businessTime.close.slice(0, 5);

  if (open === '00:00' && close === '00:00') return '24시간';
  return `${open} - ${close}`;
};
</script>

<template>
  <ul class="store-list-ul search-store-card" @click="goToProduct(props.storeInfo)">
    <li class="left store-card-left">
      <img
        class="store-image"
        :src="
          props.storeInfo.files
            ? getImagePath(props.storeInfo.files[0].storedFileName, 'origin')
            : require('@/assets/static/images/skeleton-food03.svg')
        "
        alt="상점이미지"
      />
    </li>
    <li class="right store-card-content">
      <div class="name store-card-name" name="상점 이름">
        {{ Util.translator(props.storeInfo, 'name') }}
      </div>

      <div class="store-meta" name="평점, 리뷰수, 배달 시간, 거리">
        <span class="rating">
          <i class="i-star-12" aria-hidden="true"></i>
          <span class="rating-score">{{ props.storeInfo.star.toFixed(1) ?? '0.0' }}</span>
          <span class="review-count">({{ props.storeInfo.reviewCount ?? 0 }})</span>
        </span>
        <span class="delivery-time">
          <i class="i-time" aria-hidden="true"></i>
          <span class="delivery-duration">
            {{ props.storeInfo.minimumDeliveryDuration }}
            {{ $t('search.result.card.minute') }} ~
            {{ props.storeInfo.maximumDeliveryDuration }}
            {{ $t('search.result.card.minute') }}
          </span>
        </span>
        <span class="distance">
          {{
            Util.getDistance(
              props.userPosition.latitude,
              props.userPosition.longitude,
              props.storeInfo.latitude,
              props.storeInfo.longitude
            ).toFixed(1)
          }}km
        </span>
      </div>

      <div class="order-info" name="배달팁, 최소주문금액">
        <span class="order-info-item delivery-fee-item">
          <span class="order-label">{{ $t('search.result.card.delivery_fee') }}</span>
          <span v-if="!props.storeInfo.isNegoDeliveryFee" class="delivery-fee">
            {{ calcDeliveryFee(props.storeInfo) }}
          </span>
          <span v-else class="delivery-fee">
            {{ $t('search.result.card.nego') }}
          </span>
        </span>
        <span class="order-info-item minimum-order-item">
          <span class="order-label">{{ $t('search.result.card.required_pay_amount') }}</span>
          <span class="minimum-price">
            {{ toVn(props.storeInfo.minimumPriceForDelivery) }}
          </span>
        </span>
      </div>

      <ul class="store-state">
        <li
          class="status-tag business-status yd-badge yd-badge--business"
          name="영업 상태"
          v-if="props.storeInfo.businessStatus"
          :class="props.storeInfo.businessStatus.toLowerCase()"
        >
          {{ $t(`search.main.card.store_status.${props.storeInfo.businessStatus.toLowerCase()}`) }}
          <span v-if="formatBusinessTime(props.storeInfo.businessSchedule)" class="business-time">
            {{ formatBusinessTime(props.storeInfo.businessSchedule) }}
          </span>
        </li>
        <li
          class="status-tag packing-status yd-badge yd-badge--packing"
          name="포장 가능"
          v-if="props.storeInfo.isSupportPacking"
        >
          {{ $t('search.result.card.packing_short') }}
        </li>
        <li
          class="status-tag payment-status yd-badge yd-badge--online"
          name="온라인 결제"
          v-if="props.storeInfo.isViaOnlinePayment"
        >
          {{ $t('search.result.card.online_short') }}
        </li>
      </ul>
    </li>
  </ul>
</template>

<style scoped>
.search-store-card.store-list-ul {
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  width: 100%;
  min-width: 0;
  margin: 0;
  padding: 0.8rem;
  border: 1px solid #eceff1;
  border-radius: 1.2rem;
  background-color: #fff;
  box-shadow: 0 0.2rem 0.8rem rgba(38, 50, 56, 0.12);
  cursor: pointer;
}

.search-store-card.store-list-ul > .store-card-left {
  flex: 0 0 8rem;
  width: 8rem;
  height: 8rem;
  min-width: 0;
  margin-right: 1.2rem;
  overflow: hidden;
  border-radius: 0.8rem;
  align-self: center;
}

.search-store-card.store-list-ul > .store-card-left > .store-image {
  display: block;
  width: 8rem;
  height: 8rem;
  border-radius: 0.8rem;
  background-color: #f5f5f5;
  object-fit: cover;
}

.search-store-card > .store-card-content {
  flex: 1 1 auto;
  width: auto;
  min-width: 0;
}

.search-store-card > .store-card-content .store-card-name {
  display: block;
  margin: 0;
  overflow: visible;
  color: #212121;
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.4;
  text-overflow: clip;
  white-space: normal;
  word-break: normal;
  overflow-wrap: anywhere;
}

.store-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem 0.8rem;
  min-width: 0;
  margin-top: 0.2rem;
  color: #424242;
  font-size: 1.2rem;
  line-height: 1.45;
}

.rating,
.delivery-time,
.distance {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.rating .i-star-12,
.delivery-time .i-time {
  flex: 0 0 auto;
  margin-right: 0.3rem;
}

.rating-score {
  color: #212121;
  font-weight: 700;
}

.review-count {
  min-width: 0;
  margin-left: 0.2rem;
  color: #616161;
  overflow-wrap: anywhere;
}

.delivery-duration {
  min-width: 0;
  overflow-wrap: anywhere;
}

.distance::before {
  width: 1px;
  height: 1rem;
  margin-right: 0.8rem;
  background-color: #cfd8dc;
  content: '';
}

.order-info {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.3rem 1.2rem;
  min-width: 0;
  margin-top: 0.2rem;
  color: #4f4f4f;
  font-size: 1.2rem;
  line-height: 1.45;
}

.order-info-item {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: baseline;
  column-gap: 0.4rem;
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.order-label {
  font-weight: 500;
}

.delivery-fee,
.minimum-price {
  min-width: 0;
  font-size: 1.2rem;
  font-weight: 700;
  overflow-wrap: anywhere;
}

.delivery-fee {
  color: #D96C6C;
}

.minimum-price {
  color: #00AEDD;
}

.store-state {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  min-width: 0;
  margin: 0.2rem 0 0;
  padding: 0;
  font-size: 1.2rem;
  line-height: 1.35;
}

.status-tag {
  max-width: 100%;
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  background-color: #f1f3f4;
  color: #546e7a;
  font-size: 1.2rem;
  font-weight: 500;
  overflow-wrap: anywhere;
}

.business-time {
  margin-left: 0.4rem;
}

.business-status.open {
  background-color: #e8f7ed;
  color: #257a47;
}

.business-status.close,
.business-status.ready,
.business-status.day_off {
  background-color: #eeeeee;
  color: #616161;
}

.business-status.break {
  background-color: #fff3dc;
  color: #8a5a14;
}

.packing-status {
  background-color: #eaf9fd;
  color: #167f8a;
}

.payment-status {
  background-color: #eef2ff;
  color: #4e5c8a;
}
</style>
